import { z } from 'zod';
import { SetupChange } from './sector-setup.js';

/**
 * An Executive's delegates (APP-274): the people who enter awards for them, on a Sector Lead's
 * terms. They live in their own table, so a change here is the change the app runs on; the
 * `executive.delegates` registry key only carries who may view and edit.
 */

const Ref = z.object({ id: z.string(), name: z.string() });
const Reason = z.string().trim().max(2000);

/** The award types a delegate may enter for their Executive. A Spot nomination is everyone's. */
export const ExecutiveDelegateAwardType = z.enum(['EDB', 'SDB', 'PAA', 'TAA']);
export type ExecutiveDelegateAwardType = z.infer<typeof ExecutiveDelegateAwardType>;

export const ExecutiveDelegate = z.object({
  person: z.object({
    id: z.uuid(),
    name: z.string(),
    jobTitle: z.string().nullable(),
    sector: Ref.nullable(),
  }),
  awardTypes: z.array(ExecutiveDelegateAwardType),
  /** Their entries go to the Executive first. Off by default, as a Sector Lead's are. */
  needsApproval: z.boolean(),
  /** Off: they can't enter awards for this Executive until it's turned on again. */
  active: z.boolean(),
  lastChanged: SetupChange.nullable(),
});
export type ExecutiveDelegate = z.infer<typeof ExecutiveDelegate>;

export const ExecutiveDelegateSetup = z.object({
  executive: Ref,
  canEdit: z.boolean(),
  delegates: z.array(ExecutiveDelegate),
  /** Organization availability for each type; never contains private audience members. */
  availability: z.array(
    z.object({
      type: ExecutiveDelegateAwardType,
      state: z.enum(['available', 'award_off', 'audience_off']),
    }),
  ),
});
export type ExecutiveDelegateSetup = z.infer<typeof ExecutiveDelegateSetup>;

/** The Executives whose delegates this person may open: themselves first. */
export const ExecutiveDelegateList = z.object({
  executives: z.array(Ref.extend({ canEdit: z.boolean() })),
});
export type ExecutiveDelegateList = z.infer<typeof ExecutiveDelegateList>;

export const ExecutiveParams = z.object({ executiveId: z.uuid() });
export const ExecutiveDelegateParams = ExecutiveParams.extend({ personId: z.uuid() });

/** All four types are selected by default; actual entry also needs organization availability. */
export const AddExecutiveDelegate = z
  .object({
    personId: z.uuid(),
    awardTypes: z.array(ExecutiveDelegateAwardType).min(1).max(4).optional(),
    needsApproval: z.boolean().optional(),
    reason: Reason.optional(),
  })
  .strict();
export type AddExecutiveDelegate = z.infer<typeof AddExecutiveDelegate>;

export const UpdateExecutiveDelegate = z
  .object({
    awardTypes: z.array(ExecutiveDelegateAwardType).min(1).max(4).optional(),
    needsApproval: z.boolean().optional(),
    active: z.boolean().optional(),
    reason: Reason.optional(),
  })
  .strict()
  .refine(
    (v) => v.awardTypes !== undefined || v.needsApproval !== undefined || v.active !== undefined,
    'Change at least one thing',
  );
export type UpdateExecutiveDelegate = z.infer<typeof UpdateExecutiveDelegate>;
